(() => {
  const cartActions = new Map();
  const wishlistActions = new Map();
  let cartQueue = Promise.resolve();

  function failure(code) {
    const error = new Error(code);
    error.code = code;
    return error;
  }

  function variantId(product) {
    const value = String(product?.variantId || '');
    if (!/^\d+$/.test(value) || !Number.isSafeInteger(Number(value)) || Number(value) <= 0) {
      throw failure('unavailable');
    }
    return Number(value);
  }

  function wishlistAvailable() {
    return typeof window.WishlistKing?.addWishlistItem === 'function'
      && typeof window.WishlistKing?.state?.observeWishlist === 'function';
  }

  function wishlistProduct(product) {
    const id = variantId(product);
    if (typeof product?.handle !== 'string' || !product.handle.trim()) throw failure('unavailable');
    return { productHandle: product.handle, variantId: id };
  }

  function matchingItem(wishlist, product) {
    return wishlist.items.find((item) => item.product?.handle === product.productHandle
      && String(item.variant?.id || item.variantId || '') === String(product.variantId));
  }

  // The SDK observable can emit synchronously. Defer cleanup until subscribe()
  // has returned its subscription, and ignore incomplete initialization states.
  function observeWishlist(predicate = () => true) {
    if (!wishlistAvailable()) return Promise.reject(failure('wishlist_unavailable'));
    return new Promise((resolve, reject) => {
      let subscription;
      let settled = false;
      const cleanup = () => {
        try { subscription?.unsubscribe?.(); } catch { /* SDK cleanup is best effort. */ }
      };
      const finish = (error, wishlist) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        queueMicrotask(cleanup);
        if (error) reject(error);
        else resolve(wishlist);
      };
      const timeout = setTimeout(() => finish(failure('wishlist_unavailable')), 5000);
      try {
        subscription = window.WishlistKing.state.observeWishlist({ wishlistId: 'mine' }).subscribe((wishlist) => {
          if (Array.isArray(wishlist?.items) && predicate(wishlist)) finish(null, wishlist);
        });
      } catch {
        finish(failure('wishlist_unavailable'));
      }
    });
  }

  async function isInWishlist(product) {
    const selected = wishlistProduct(product);
    const wishlist = await observeWishlist();
    return Boolean(matchingItem(wishlist, selected));
  }

  async function saveToWishlist(product) {
    const selected = wishlistProduct(product);
    const key = `${selected.productHandle}:${selected.variantId}`;
    if (wishlistActions.has(key)) return wishlistActions.get(key);
    const action = (async () => {
      const wishlist = await observeWishlist();
      const existing = matchingItem(wishlist, selected);
      if (existing) return { saved: true, alreadySaved: true, wishlistItem: existing };
      let result;
      try {
        result = await window.WishlistKing.addWishlistItem(selected);
      } catch {
        throw failure('wishlist_unavailable');
      }
      let item = result?.wishlistItem;
      if (!item?.id) {
        const updated = await observeWishlist((value) => Boolean(matchingItem(value, selected)));
        item = matchingItem(updated, selected);
      }
      return { saved: true, alreadySaved: false, wishlistItem: item };
    })();
    wishlistActions.set(key, action);
    try {
      return await action;
    } finally {
      wishlistActions.delete(key);
    }
  }

  function rootUrl() {
    return window.FoxTheme?.routes?.root_url || window.Shopify?.routes?.root || '/';
  }

  function notify(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  async function refreshCart() {
    try {
      const response = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const cart = await response.json();
      if (!Number.isInteger(cart.item_count) || !Array.isArray(cart.items)) return null;
      // CartItems installs its theme focus trap when given section HTML. Publish
      // only cart data here, then refresh the drawer without opening it.
      delete cart.sections;
      try {
        const pubsub = window.FoxTheme?.pubsub;
        if (pubsub?.PUB_SUB_EVENTS?.cartUpdate) pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
      } catch { /* A theme subscriber must not turn a successful addition into a retry. */ }
      notify('cart:updated', { cart });
      return cart;
    } catch {
      return null;
    }
  }

  async function performCartAddition(id) {
    let response;
    let result;
    try {
      response = await fetch(`${rootUrl()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });
      result = await response.json();
    } catch {
      throw failure('network');
    }
    if (!response.ok || result.status) {
      const message = String(result.description || result.message || '');
      const soldOut = /sold out|out of stock|not enough|inventory|can(?:not|'t) add more|already in your cart/i.test(message);
      throw failure(soldOut ? 'sold_out' : response.status >= 500 ? 'network' : 'unavailable');
    }
    const item = result.items?.[0] || result;
    const cart = await refreshCart();
    notify('cart:refresh', { open: false });
    notify('product-ajax:added', { product: item });
    return { item, cart, refreshed: cart !== null };
  }

  async function addToCart(product) {
    const id = variantId(product);
    if (cartActions.has(id)) return cartActions.get(id);
    // Serialize different additions so older cart snapshots cannot overwrite
    // counts from a later request. Repeated clicks for one variant share work.
    const action = cartQueue.then(() => performCartAddition(id));
    cartQueue = action.catch(() => {});
    cartActions.set(id, action);
    try {
      return await action;
    } finally {
      cartActions.delete(id);
    }
  }

  window.LampsRoomCommerce = { addToCart, saveToWishlist, isInWishlist, wishlistAvailable };
})();
